import {
    AnalyticsCTAEvent,
    useTrackCTAClickedAnalytics,
} from '@/utils/amplitude/analytics/cta/trackCTAClicked.analytics';
import { BidCanvas } from '@liveauctioneers/hammer-ui-liveauctioneers/lotDetail';
import { getRegistrationNotify } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { MaxBidSelector } from '@/components/ItemBidding/components/BidCanvasContainer/components/MaxBidSelector';
import { openCompleteAccountSignUpModal } from '@/redux/modules/modal';
import { temporaryBidderMaxBidAtom } from '@/components/Modals/AuthModal/AbsenteeBid/pages/AbsenteeBid/components/PlaceBidForm/components/SelectBidDropdown/SelectBidDropdown';
import { toggleCatalogNotifications } from '@/redux/modules/catalog/registration/catalogRegistration.actions';
import { TrackPlaceBidPressedButtonType } from '@/utils/amplitude/analytics';
import { useAddToCalendarClicked } from './hooks/useHandleBidCanvasCallbacks/useAddToCalendarClicked';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useBuyNowCallbacks } from './hooks/useHandleBidCanvasCallbacks/useBuyNowCallbacks';
import { useCallback } from 'react';
import { useEnterLiveAuction } from './hooks/useHandleBidCanvasCallbacks/useEnterLiveAuction';
import { useGetBidCanvasBuyerData } from './hooks/useGetBidCanvasBuyerData';
import { useGetBidCanvasCatalogData } from './hooks/useGetBidCanvasCatalogData';
import { useGetBidCanvasItemData } from './hooks/useGetBidCanvasItemData';
import { useHandleLinkClicked } from './hooks/useHandleBidCanvasCallbacks/useHandleLinkClicked';
import { usePlaceBid } from './hooks/useHandleBidCanvasCallbacks/usePlaceBid';
import { useRegisterForAuction } from './hooks/useHandleBidCanvasCallbacks/useRegisterForAuction';
import { useSeeSoldPrice } from './hooks/useHandleBidCanvasCallbacks/useSeeSoldPrice';
import { useSendSellerMessage } from './hooks/useHandleBidCanvasCallbacks/useSendSellerMessage';
import { useSetAtom } from 'jotai';
import { useViewOrRegisterOnPartnerSite } from './hooks/useHandleBidCanvasCallbacks/useViewOrRegisterOnPartnerSite';

type Props = {
    catalogId: number;
    className?: string;
    itemId: number;
    onCountdownEnd: () => void;
};

export const BidCanvasContainer = ({ catalogId, className, itemId, onCountdownEnd }: Props) => {
    const dispatch = useAppDispatch();
    const buyer = useGetBidCanvasBuyerData({ itemId });
    const catalog = useGetBidCanvasCatalogData({ itemId });
    const item = useGetBidCanvasItemData({ itemId, onCountdownEnd });
    const trackCTAClicked = useTrackCTAClickedAnalytics();
    const notify = useAppSelector((state) => getRegistrationNotify(state, catalogId));

    const setTemporaryBidderMaxBidAtom = useSetAtom(temporaryBidderMaxBidAtom);

    // CALLBACKS
    const onPlaceBidClicked = usePlaceBid({ itemId });
    const onEnterLiveAuctionClicked = useEnterLiveAuction({ catalogId });
    const onLinkClickedFunction = useHandleLinkClicked({ itemId });
    const onRegisterForAuctionClicked = useRegisterForAuction({ catalogId, itemId });
    const onSeeSoldPriceClicked = useSeeSoldPrice({ itemId });
    const onSendMessageClicked = useSendSellerMessage({ itemId });
    const onRegisterOnPartnerSiteClickedFunction = useViewOrRegisterOnPartnerSite({ catalogId, itemId });
    const { onBuyNowClicked, onMakeAnOfferClicked, onViewMoreBuyItNowItemsClicked } = useBuyNowCallbacks({ itemId });
    const onAddToCalendarClicked = useAddToCalendarClicked({ catalogId });

    const onRegisterOnPartnerSiteClicked = useCallback(() => {
        onRegisterOnPartnerSiteClickedFunction('register');
    }, [onRegisterOnPartnerSiteClickedFunction]);

    const onViewOnPartnerSiteClicked = useCallback(() => {
        onRegisterOnPartnerSiteClickedFunction('view');
    }, [onRegisterOnPartnerSiteClickedFunction]);

    const onSellSimilarItemClicked = useCallback(() => {
        onLinkClickedFunction('sell_similar_item');
    }, [onLinkClickedFunction]);

    const onTipsForGettingApprovedClicked = useCallback(() => {
        onLinkClickedFunction('tips_for_getting_approved');
    }, [onLinkClickedFunction]);

    const onTipsForWinningClicked = useCallback(() => {
        onLinkClickedFunction('tips_for_winning');
    }, [onLinkClickedFunction]);

    const onCompleteProfileClicked = useCallback(() => {
        dispatch(openCompleteAccountSignUpModal());
    }, [dispatch]);

    const handleToggleNotifications = useCallback(() => {
        return dispatch(toggleCatalogNotifications({ catalogId, notify: !notify }));
    }, [catalogId, dispatch, notify]);

    const wrappedRegisterForAuctionClick = useCallback(() => {
        // Track the CTA click
        trackCTAClicked(AnalyticsCTAEvent.ItemPage.RegisterForAuction);

        // Then call the original handler
        onRegisterForAuctionClicked();
    }, [onRegisterForAuctionClicked, trackCTAClicked]);

    const onBidPanelChipIncrementsClicked = useCallback(
        (bidAmount: number, buttonType?: TrackPlaceBidPressedButtonType) => {
            setTemporaryBidderMaxBidAtom(bidAmount);
            onPlaceBidClicked(buttonType);
        },
        [onPlaceBidClicked, setTemporaryBidderMaxBidAtom]
    );

    return (
        <div className={className}>
            <BidCanvas
                buyer={buyer}
                callbacks={{
                    onAddToCalendarClicked,
                    onBidPanelChipIncrementsClicked,
                    onBuyNowClicked,
                    onCompleteProfileClicked,
                    onEnterLiveAuctionClicked,
                    onFollowClicked: handleToggleNotifications,
                    onLoginClicked: onRegisterForAuctionClicked,
                    onMakeAnOfferClicked,
                    onPlaceBidClicked: () => {
                        onPlaceBidClicked('Place Bid Button');
                    },
                    onRegisterForAuctionClicked: wrappedRegisterForAuctionClick,
                    onRegisterOnPartnerSiteClicked,
                    onSeeSoldPriceClicked,
                    onSellSimilarItemClicked,
                    onSendMessageClicked,
                    onSignUpClicked: onRegisterForAuctionClicked,
                    onTipsForGettingApprovedClicked,
                    onTipsForWinningClicked,
                    onViewMoreBuyItNowItemsClicked,
                    onViewOnPartnerSiteClicked,
                }}
                catalog={catalog}
                item={item}
                variations={{
                    bidFieldVariant: {
                        bidFieldMaxBidComponent: <MaxBidSelector itemId={itemId} />,
                        enabled: true,
                    },
                    hasAdvancedStickyMobileBidButton: false,
                    hasAutoApproveWithBidLimitExperiment: false,
                    hasMobileLotNavigation: false,
                    hasStickyMobileBidButton: false,
                    showNoReservePriceBadge: item.showNoReserve,
                }}
            />
        </div>
    );
};
